import { useCallback, useEffect, useState } from "react";
import RecentTracker from "@/components/RecentTracker";
import { RecordSwitcher } from "@/components/RecordSwitcher";
import { useUrlState } from "@/lib/useUrlState";
import axios from "axios";
import { Loader2, Hash } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { opsApi, money } from "./lib/api";
import { useAuth } from "@/lib/AuthContext";
import InfoTab from "./tabs/InfoTab";
import EstimateTab from "./tabs/EstimateTab";
import DocumentsTab from "./tabs/DocumentsTab";
import FrsTab from "./tabs/frs/FrsTab";
import DrawingsTab from "../drawings/DrawingsTab";
import BudgetTab from "./tabs/budget/BudgetTab";
import ChangeOrdersTab from "./tabs/co/ChangeOrdersTab";
import IntakeTab from "./tabs/intake/IntakeTab";
import SubmittalsTab from "./tabs/submittals/SubmittalsTab";
import PurchasingTab from "./tabs/purchasing/PurchasingTab";
import InvoicingTab from "./tabs/invoicing/InvoicingTab";
import ProjectSettingsTab from "./tabs/ProjectSettingsTab";
import Placeholder from "./tabs/Placeholder";
import StatusDialog from "./components/StatusDialog";
import StatusMenu from "./components/StatusMenu";
import EmailDialog from "./components/EmailDialog";

const TABS = [
  { key: "info", label: "Info" },
  { key: "estimate", label: "Estimate" },
  { key: "frs", label: "FRS" },
  { key: "drawings", label: "Drawings" },
  { key: "submittals", label: "Submittals", cluster: "OPS-4" },
  { key: "intake", label: "Intake" },
  { key: "change_orders", label: "Change Orders" },
  { key: "purchasing", label: "Purchasing", cluster: "OPS-5" },
  { key: "budget", label: "Budget" },
  { key: "invoicing", label: "Invoicing" },
  { key: "records", label: "Records" },
  { key: "activity", label: "Activity" },
];
const BUILT = ["info", "estimate", "frs", "drawings", "budget", "change_orders", "intake", "submittals", "purchasing",
  "invoicing", "records", "activity"];
// ROLES-1: which matrix cell each project tab answers to (Info / Records / Activity share one)
const TAB_CELL = { info: "operations.projects", records: "operations.projects", activity: "operations.projects",
  estimate: "operations.estimate", frs: "operations.frs", drawings: "operations.drawings",
  submittals: "operations.submittals", change_orders: "operations.change_orders", intake: "operations.change_orders",
  purchasing: "operations.purchasing", budget: "operations.budget", invoicing: "operations.invoicing" };
// Role-based default order: the role's own work first (ROLES-1, approved defaults)
const ROLE_TAB_ORDER = {
  project_engineer: ["info", "submittals", "purchasing", "frs", "drawings", "change_orders", "intake", "estimate", "budget", "invoicing", "records", "activity"],
};

export default function ProjectDetail({ projectId, initialTab = "", onSwitch }) {
  const [project, setProject] = useState(null);
  const [jobs, setJobs] = useState([]);
  useEffect(() => { opsApi.listProjects({ status: "" }).then((d) => setJobs(d.projects || [])).catch(() => {}); }, []);
  const [tab, setTab] = useUrlState("otab", initialTab || "info");
  const { principal } = useAuth();
  // ROLES-1: hide tabs the matrix sets to none; order the role's own work first
  const visibleTabs = (() => {
    const cells = principal?.tabs || {};
    const order = ROLE_TAB_ORDER[principal?.role];
    const list = order ? order.map((k) => TABS.find((t) => t.key === k)).filter(Boolean) : TABS;
    return list.filter((t) => !principal || ["admin", "management"].includes(principal.role) || !cells[TAB_CELL[t.key]]
      || cells[TAB_CELL[t.key]] !== "none");
  })();
  useEffect(() => {
    if (visibleTabs.length && !visibleTabs.some((t) => t.key === tab)) setTab(visibleTabs[0].key);
  }, [visibleTabs, tab]);
  const [frsFocus, setFrsFocus] = useState(null);   // { room, product } from a plan link
  const [statusTarget, setStatusTarget] = useState(null);
  const [email, setEmail] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setProject(await opsApi.getProject(projectId));
  }, [projectId]);

  useEffect(() => { load(); }, [load]);

  // PRC-REV — one-click jumps from a review finding to its evidence (FRS row, CO, PO, estimate)
  useEffect(() => {
    const jump = (e) => {
      const d = e.detail || {};
      if (!d.tab) return;
      if (d.tab === "frs") setFrsFocus({ room: d.room || "", product: d.product || "", from: "plan" });
      setTab(d.tab);
    };
    window.addEventListener("abs:ops-jump", jump);
    return () => window.removeEventListener("abs:ops-jump", jump);
  }, []);

  const assignNumber = async () => {
    setBusy(true);
    try {
      const token = localStorage.getItem("auth_token");
      const { data } = await axios.post(`${window.location.origin}/api/projects/${projectId}/assign-job-number`,
        {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`Job number ${data.job_number} assigned`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not assign a job number");
    } finally {
      setBusy(false);
    }
  };

  const requestOpen = async () => {
    try {
      setEmail(await opsApi.projectEmailDraft(projectId, "opening_request"));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not build that email");
    }
  };

  if (!project) {
    return (
      <div className="flex items-center justify-center gap-2 py-24 text-sm text-slate-500">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading job…
      </div>
    );
  }


  return (
    <div className="h-full space-y-5 overflow-y-auto pb-6 pl-4 pr-4 pt-3" data-testid="ops-project-detail">
      <RecentTracker kind="ops_job" refId={projectId} label={project.identity.name}
                     sub={project.identity.job_number ? `Jobs · ${project.identity.job_number}` : "Jobs"} path={`/jobs?job=${projectId}`} />
      <div className="rounded-lg border border-slate-200 bg-white px-4 py-2.5">
        <div className="flex items-center gap-4">
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <RecordSwitcher variant="title" currentId={projectId} testid="ops-job-switcher" empty="No jobs match."
                              currentLabel={`${project.identity.job_number ? `${project.identity.job_number} ` : ""}${project.identity.name}`}
                              items={jobs.map((j) => ({ id: j.id, label: `${j.job_number ? `${j.job_number} ` : ""}${j.name}`, meta: j.status_label }))}
                              onPick={(id) => onSwitch?.(id)} />
            </div>
            <div className="mt-0.5 text-[11px] leading-tight text-slate-500" data-testid="ops-project-location">
              {[project.identity.address, project.identity.city, [project.identity.state, project.identity.zip_code].filter(Boolean).join(" ")]
                .filter(Boolean).join(", ") || "No address yet"}
            </div>
          </div>
          <div className="ml-auto flex shrink-0 items-center gap-3">
            {project.can_assign_job_number && (
              <Button variant="outline" size="sm" onClick={assignNumber} disabled={busy}
                      data-testid="ops-assign-job-number">
                <Hash className="mr-1.5 h-3.5 w-3.5" /> Assign job number
              </Button>
            )}
            <StatusMenu project={project} onRequestOpen={requestOpen} hideLabel align="right"
                        onPick={(t) => setStatusTarget(t.status)} />
            <span className="text-sm font-semibold tabular-nums text-slate-900" data-testid="ops-project-total">
              {money(project.financials.current_contract)}
            </span>
          </div>
        </div>
      </div>

      {/* Tabs — boxed, same as the Proposals detail tabs */}
      <div className="flex flex-wrap items-center gap-1 border-b border-slate-200">
        {visibleTabs.map((t) => (
          <button key={t.key} onClick={() => setTab(t.key)} data-testid={`ops-tab-${t.key}`}
                  className={`flex items-center gap-2 whitespace-nowrap border-b-2 -mb-px px-3 py-3 text-sm transition-colors ${
                    tab === t.key ? "border-[#007BFF] text-[#007BFF] font-semibold"
                      : "border-transparent text-slate-500 hover:text-slate-800 font-medium"}`}>
            {t.label}
          </button>
        ))}
      </div>

      {tab === "info" && <InfoTab project={project} reload={load} onGo={setTab} />}
      {tab === "estimate" && <EstimateTab project={project} reload={load} />}
      {tab === "frs" && <FrsTab project={project} reload={load} focus={frsFocus} onOpenPlan={(room, product) => { setFrsFocus({ room, product, from: "ops-frs" }); setTab("drawings"); }} />}
      {tab === "drawings" && (
        <DrawingsTab projectId={project.id} me={localStorage.getItem("fieldops-installer-name") || ""} isField={false} canWrite
                     link={frsFocus?.from === "ops-frs" ? { room: frsFocus.room, product: frsFocus.product } : null}
                     onLinkConsumed={() => setFrsFocus((f) => (f ? { ...f, from: "" } : f))}
                     onOpenFrs={(room, product, scopes) => { setFrsFocus({ room, product, scopes: scopes || [], from: "plan" }); setTab("frs"); }} />
      )}
      {tab === "budget" && <BudgetTab project={project} reload={load} />}
      {tab === "change_orders" && <ChangeOrdersTab project={project} reload={load} />}
      {tab === "intake" && <IntakeTab project={project} />}
      {tab === "submittals" && <SubmittalsTab project={project} reload={load} />}
      {tab === "purchasing" && <PurchasingTab project={project} reload={load} />}
      {tab === "invoicing" && <InvoicingTab project={project} reload={load} />}
      {tab === "activity" && <ProjectSettingsTab project={project} reload={load} />}
      {tab === "records" && <DocumentsTab project={project} reload={load} />}
      {!BUILT.includes(tab) && <Placeholder tab={TABS.find((t) => t.key === tab)} />}

      {statusTarget && (
        <StatusDialog project={project} initialTarget={statusTarget}
                      onClose={() => setStatusTarget(null)}
                      onDone={async (drafts) => {
                        setStatusTarget(null);
                        await load();
                        if (drafts?.accounting_email) setEmail(drafts.accounting_email);
                      }} />
      )}
      {email && (
        <EmailDialog projectId={projectId} draft={email} onClose={() => setEmail(null)} />
      )}
    </div>
  );
}
